import { useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import toast from "react-hot-toast";
import { ModalWrapper } from "../../../components/modals/ModalWrapper";
import { CustomerInterface } from "../OrderDetailsInterface";
import { dictionary } from "../../../utils/dictionary";
import { useAuth } from "../../../context/AuthContext";
import { handleApi } from "../../../utils/handleApi";
import { retryMastershopOrder, updateOrderCustomer } from "../../../services/orders/orderAxios";

interface RetryForm {
  name: string;
  lastName: string;
  phone: string;
  address: string;
  addressDetail: string;
  neighborhood: string;
  department: string;
  city: string;
  documentType: string;
  documentNumber: string;
}

// Tipos de documento que acepta Mastershop (allowlist del backend).
const MASTERSHOP_DOCUMENT_TYPES = ["CC", "CE", "TI", "PAS", "NIT", "NITEXT", "DIE", "RC", "TE"];

const normalizeFieldKey = (key: unknown) =>
  String(key ?? "").trim().toLowerCase().replace(/[_\s]/g, "");

const customFieldValue = (customer: CustomerInterface, key: string) => {
  const target = normalizeFieldKey(key);
  const field = (customer.custom_fields ?? []).find(
    (item) => normalizeFieldKey(item?.key) === target
  );
  return field?.value != null ? String(field.value) : "";
};

// Mismo flujo que el retry de Dropi: corregir los datos del cliente y volver
// a despachar. Mastershop valida campos (p. ej. departamento) al recibir la
// orden, así que reintentar sin editar suele fallar igual.
export const MastershopRetryModal = ({
  orderId,
  customer,
  onClose,
  onSynced,
}: {
  orderId: number;
  customer: CustomerInterface;
  onClose: () => void;
  onSynced: () => void;
}) => {
  const { language } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState<RetryForm>({
    name: customer.name || "",
    lastName: customer.last_name || "",
    phone: customer.phone || "",
    address: customer.address || "",
    addressDetail: customer.address_detail || "",
    neighborhood: customer.neighborhood || "",
    department: customer.department || "",
    city: customer.city || "",
    documentType: customFieldValue(customer, "document_type").toUpperCase(),
    documentNumber: customFieldValue(customer, "document_number"),
  });

  const setField = (field: keyof RetryForm, value: string) => {
    setForm((current) => ({
      ...current,
      [field]: field === "phone" ? value.replace(/\D/g, "") : value,
    }));
  };

  const validateForm = () => {
    const requiredFields: Array<[keyof RetryForm, string]> = [
      ["name", dictionary["name"]?.[language] || "Nombre"],
      ["phone", dictionary["phone-label"]?.[language] || "Teléfono"],
      ["address", dictionary["shipping-address"][language]],
      ["department", dictionary["department"]?.[language] || "Departamento"],
      ["city", dictionary["city"]?.[language] || "Ciudad"],
      ["documentType", dictionary["document-type-label"][language]],
      ["documentNumber", dictionary["document-number-label"][language]],
    ];

    const emptyField = requiredFields.find(([field]) => !form[field].trim());
    if (emptyField) {
      toast.error(dictionary["field-required"][language].replace("{field}", emptyField[1]));
      return false;
    }
    return true;
  };

  const handleRetry = async () => {
    if (isSubmitting || !validateForm()) return;

    setIsSubmitting(true);
    const { error: updateError } = await handleApi(
      updateOrderCustomer(orderId, {
        name: form.name.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone.trim(),
        address: form.address.trim(),
        addressDetail: form.addressDetail.trim(),
        neighborhood: form.neighborhood.trim(),
        department: form.department.trim(),
        city: form.city.trim(),
        documentType: form.documentType.trim(),
        documentNumber: form.documentNumber.trim(),
        source: "mastershop_manual_retry",
      })
    );

    if (!updateError) {
      const { error: retryError } = await handleApi(retryMastershopOrder(orderId));
      if (!retryError) {
        toast.success(dictionary["mastershop-retry-success"][language]);
        onSynced();
        onClose();
      }
    }
    setIsSubmitting(false);
  };

  const inputClass =
    "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-fluxi/40 focus:border-fluxi";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";

  return (
    <ModalWrapper isOpen onClose={onClose}>
      <div className="max-h-[85vh] overflow-y-auto p-6">
        <h3 className="text-lg font-semibold pr-8">
          {dictionary["mastershop-retry-modal-title"][language]}
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          {dictionary["mastershop-retry-modal-hint"][language]}
        </p>

        <div className="mt-5 grid grid-cols-1 gap-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className={labelClass}>{dictionary["name"]?.[language] || "Nombre"}</label>
              <input
                type="text"
                value={form.name}
                onChange={(event) => setField("name", event.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>
                {dictionary["last-name"]?.[language] || "Apellido"}
              </label>
              <input
                type="text"
                value={form.lastName}
                onChange={(event) => setField("lastName", event.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>
              {dictionary["phone-label"]?.[language] || "Teléfono"}
            </label>
            <input
              type="text"
              value={form.phone}
              onChange={(event) => setField("phone", event.target.value)}
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>{dictionary["shipping-address"][language]}</label>
            <input
              type="text"
              value={form.address}
              onChange={(event) => setField("address", event.target.value)}
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>
              {dictionary["address-detail"]?.[language] || "Detalle"}
            </label>
            <input
              type="text"
              value={form.addressDetail}
              onChange={(event) => setField("addressDetail", event.target.value)}
              className={inputClass}
            />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className={labelClass}>
                {dictionary["department"]?.[language] || "Departamento"}
              </label>
              <input
                type="text"
                value={form.department}
                onChange={(event) => setField("department", event.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>{dictionary["city"]?.[language] || "Ciudad"}</label>
              <input
                type="text"
                value={form.city}
                onChange={(event) => setField("city", event.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className={labelClass}>
                {dictionary["document-type-label"][language]}
              </label>
              <select
                value={form.documentType}
                onChange={(event) => setField("documentType", event.target.value)}
                className={`${inputClass} bg-white`}
              >
                <option value="">—</option>
                {MASTERSHOP_DOCUMENT_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>
                {dictionary["document-number-label"][language]}
              </label>
              <input
                type="text"
                value={form.documentNumber}
                onChange={(event) => setField("documentNumber", event.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>
              {dictionary["neighborhood"]?.[language] || "Barrio"}
            </label>
            <input
              type="text"
              value={form.neighborhood}
              onChange={(event) => setField("neighborhood", event.target.value)}
              className={inputClass}
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
          >
            {dictionary["cancel"]?.[language]}
          </button>
          <button
            type="button"
            onClick={handleRetry}
            disabled={isSubmitting}
            className="inline-flex items-center gap-2 rounded-lg bg-fluxi px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-fluxi-dark disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            {isSubmitting
              ? dictionary["mastershop-retrying-button"][language]
              : dictionary["mastershop-retry-button"][language]}
          </button>
        </div>
      </div>
    </ModalWrapper>
  );
};
